import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { TimezoneService, DEFAULT_TIMEZONE } from '../services/timezone/timezone.service';

/**
 * Pipe que formatea fechas en la zona horaria del usuario actual.
 *
 * Se comporta exactamente como Angular `DatePipe` pero el tercer argumento
 * (timezone) se inyecta automáticamente desde {@link TimezoneService}.
 *
 * Uso en templates:
 *   {{ vacante.fechaCreado | appDate }}                    // dd/MM/yyyy default
 *   {{ vacante.fechaCreado | appDate:'mediumDate' }}
 *   {{ run.ejecutadoEn    | appDate:'dd/MM/yyyy HH:mm' }}
 *
 * Pre-requisito: registerLocaleData('es-MX') en app.module.ts y LOCALE_ID
 * provisto como 'es-MX'. Sin eso DatePipe lanza "Missing locale data".
 *
 * El pipe es {@code pure: false} para que se re-evalúe cuando el usuario cambia
 * su zona desde el perfil sin tener que recargar.
 */
@Pipe({ name: 'appDate', pure: false })
export class AppDatePipe implements PipeTransform, OnDestroy {

  private datePipe = new DatePipe('es-MX');
  private currentZone: string = DEFAULT_TIMEZONE;
  private sub: Subscription;

  constructor(
    private tz: TimezoneService,
    private cdr: ChangeDetectorRef
  ) {
    this.currentZone = this.tz.getCurrentZone();
    this.sub = this.tz.zoneChanges().subscribe(zone => {
      if (zone !== this.currentZone) {
        this.currentZone = zone;
        this.cdr.markForCheck();
      }
    });
  }

  transform(value: Date | string | number | null | undefined,
            format: string = 'dd/MM/yyyy',
            locale: string = 'es-MX'): string | null {
    // Vacíos legítimos → null (es estado, no excepción).
    if (value === null || value === undefined || value === '' || value === 'null') return null;

    // Si quien llama mandó un valor con forma de fecha pero inválido (ej. "abc",
    // 0, NaN), devolvemos null sin propagar y dejamos un warn — saber qué llegó
    // mal es lo que el usuario va a depurar; tragárselo en silencio no.
    if (typeof value === 'number' && (!isFinite(value) || value === 0)) {
      console.warn('[AppDatePipe] valor numérico inválido:', value);
      return null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const probe = new Date(value as any);
      if (isNaN(probe.getTime())) {
        console.warn('[AppDatePipe] no se pudo parsear como fecha:', value);
        return null;
      }
    }

    try {
      return this.datePipe.transform(value as any, format, this.currentZone, locale);
    } catch (e) {
      console.warn('[AppDatePipe] DatePipe lanzó NG02100 — valor:', value, 'zona:', this.currentZone, e);
      return null;
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
